import type { CSSProperties } from 'react';

import ButtonBase from '@mui/material/ButtonBase';
import type { ButtonBaseProps } from '@mui/material/ButtonBase';
import { darken } from '@mui/material/styles';

import Tooltip from '@mui/material/Tooltip';
import type { TooltipProps } from '@mui/material/Tooltip';
import type { Sx } from '@/types/helpers';

import { normalizeSx } from '@/utils/normalizeSx';

export type TooltipIconButtonProps = {
  title: string;
  sx?: Sx;
  tooltipProps?: Omit<TooltipProps, 'title' | 'children'>;
} & Omit<ButtonBaseProps, 'title' | 'aria-label'>;

type TooltipIconButtonSx = Exclude<Sx, ReadonlyArray<unknown>>;

const tooltipIconButtonSx: TooltipIconButtonSx = theme => ({
  backgroundColor: 'gx.response',
  height: 48,
  minWidth: 20,
  p: 0,
  borderRadius: 0,
  transition: 'background-color 0.13s ease',
  svg: {
    fontSize: theme.typography.pxToRem(12)
  },
  '&:hover > .MuiSvgIcon-root': {
    color: 'white'
  },
  ':hover': {
    backgroundColor: darken(theme.palette.gx.response, 0.15)
  }
});

const disabledTooltipWrapperStyle: CSSProperties = { display: 'inline-flex' };

export function TooltipIconButton({
  title,
  sx,
  tooltipProps,
  children,
  ...buttonProps
}: TooltipIconButtonProps) {
  const button = (
    <ButtonBase
      aria-label={title}
      sx={[tooltipIconButtonSx, ...normalizeSx(sx)]}
      {...buttonProps}
    >
      {children}
    </ButtonBase>
  );

  return (
    <Tooltip
      title={title}
      {...tooltipProps}
    >
      {buttonProps.disabled ? <span style={disabledTooltipWrapperStyle}>{button}</span> : button}
    </Tooltip>
  );
}

export default TooltipIconButton;
